import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Link, 
  Share2, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { footerAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import AdminSectionWrapper from './AdminSectionWrapper';

const FooterManagement = () => {
  const { notify } = useNotificationHook();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Footer data
  const [footerData, setFooterData] = useState({
    settings: {
      footer_description: '',
      footer_copyright: '',
      footer_email: '',
      footer_phone: '',
      footer_address: ''
    },
    sections: [],
    links: [],
    socialLinks: []
  });

  // Form states
  const [settingsForm, setSettingsForm] = useState({
    footer_description: '',
    footer_copyright: '',
    footer_email: '',
    footer_phone: '',
    footer_address: ''
  });

  // Modal states
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form data for modals
  const [sectionForm, setSectionForm] = useState({
    section_type: 'services',
    title: '',
    sort_order: 0
  });

  const [linkForm, setLinkForm] = useState({
    section_id: '',
    title: '',
    url: '',
    icon: '',
    sort_order: 0
  });

  const [socialForm, setSocialForm] = useState({
    name: '',
    icon: '',
    url: '',
    sort_order: 0
  });

  // Fetch footer data
  const fetchFooterData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await footerAPI.getAdminContent();
      
      if (response?.data?.success) {
        const data = response.data.data;
        setFooterData(data);
        setSettingsForm({
          footer_description: data.settings.footer_description?.value || '',
          footer_copyright: data.settings.footer_copyright?.value || '',
          footer_email: data.settings.footer_email?.value || '',
          footer_phone: data.settings.footer_phone?.value || '',
          footer_address: data.settings.footer_address?.value || ''
        });
      }
    } catch (err) {
      console.error('Error fetching footer data:', err);
      setError('Failed to load footer data');
      notify.error('Failed to load footer data', 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterData();
  }, []);

  // Handle settings save
  const handleSettingsSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await footerAPI.updateSettings(settingsForm);
      
      if (response?.data?.success) {
        setSuccess('Footer settings updated successfully');
        notify.success('Footer settings updated successfully', 'Success');
        fetchFooterData(); // Refresh data
      }
    } catch (err) {
      console.error('Error updating footer settings:', err);
      setError('Failed to update footer settings');
      notify.error('Failed to update footer settings', 'Update Error');
    } finally {
      setSaving(false);
    }
  };

  // Handle section operations
  const handleSectionSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      let response;
      if (editingItem) {
        response = await footerAPI.updateSection(editingItem.id, sectionForm);
      } else {
        response = await footerAPI.createSection(sectionForm);
      }
      
      if (response?.data?.success) {
        setSuccess(`Footer section ${editingItem ? 'updated' : 'created'} successfully`);
        notify.success(`Footer section ${editingItem ? 'updated' : 'created'} successfully`, 'Success');
        closeSectionModal();
        fetchFooterData();
      }
    } catch (err) {
      console.error('Error saving footer section:', err);
      setError('Failed to save footer section');
      notify.error('Failed to save footer section', 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const handleSectionDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await footerAPI.deleteSection(id);
      
      if (response?.data?.success) {
        setSuccess('Footer section deleted successfully');
        notify.success('Footer section deleted successfully', 'Deleted');
        fetchFooterData();
      }
    } catch (err) {
      console.error('Error deleting footer section:', err);
      setError('Failed to delete footer section');
      notify.error('Failed to delete footer section', 'Delete Error');
    } finally {
      setSaving(false);
    }
  };

  // Handle link operations
  const handleLinkSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      let response;
      if (editingItem) {
        response = await footerAPI.updateLink(editingItem.id, linkForm);
      } else {
        response = await footerAPI.createLink(linkForm);
      }
      
      if (response?.data?.success) {
        setSuccess(`Footer link ${editingItem ? 'updated' : 'created'} successfully`);
        notify.success(`Footer link ${editingItem ? 'updated' : 'created'} successfully`, 'Success');
        closeLinkModal();
        fetchFooterData();
      }
    } catch (err) {
      console.error('Error saving footer link:', err);
      setError('Failed to save footer link');
      notify.error('Failed to save footer link', 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await footerAPI.deleteLink(id);
      
      if (response?.data?.success) {
        setSuccess('Footer link deleted successfully');
        notify.success('Footer link deleted successfully', 'Deleted');
        fetchFooterData();
      }
    } catch (err) {
      console.error('Error deleting footer link:', err);
      setError('Failed to delete footer link');
      notify.error('Failed to delete footer link', 'Delete Error');
    } finally {
      setSaving(false);
    }
  };

  // Handle social link operations
  const handleSocialSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      let response;
      if (editingItem) {
        response = await footerAPI.updateSocialLink(editingItem.id, socialForm);
      } else {
        response = await footerAPI.createSocialLink(socialForm);
      }
      
      if (response?.data?.success) {
        setSuccess(`Social link ${editingItem ? 'updated' : 'created'} successfully`);
        notify.success(`Social link ${editingItem ? 'updated' : 'created'} successfully`, 'Success');
        closeSocialModal();
        fetchFooterData();
      }
    } catch (err) {
      console.error('Error saving social link:', err);
      setError('Failed to save social link');
      notify.error('Failed to save social link', 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const handleSocialDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this social link?')) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await footerAPI.deleteSocialLink(id);
      
      if (response?.data?.success) {
        setSuccess('Social link deleted successfully');
        notify.success('Social link deleted successfully', 'Deleted');
        fetchFooterData();
      }
    } catch (err) {
      console.error('Error deleting social link:', err);
      setError('Failed to delete social link');
      notify.error('Failed to delete social link', 'Delete Error');
    } finally {
      setSaving(false);
    }
  };

  // Modal handlers
  const openSectionModal = (section = null) => {
    if (section) {
      setEditingItem(section);
      setSectionForm({
        section_type: section.section_type,
        title: section.title,
        sort_order: section.sort_order
      });
    } else {
      setEditingItem(null);
      setSectionForm({
        section_type: 'services',
        title: '',
        sort_order: 0
      });
    }
    setShowSectionModal(true);
  };

  const closeSectionModal = () => {
    setShowSectionModal(false);
    setEditingItem(null);
    setSectionForm({
      section_type: 'services',
      title: '',
      sort_order: 0
    });
  };

  const openLinkModal = (link = null) => {
    if (link) {
      setEditingItem(link);
      setLinkForm({
        section_id: link.section_id,
        title: link.title,
        url: link.url || '',
        icon: link.icon || '',
        sort_order: link.sort_order
      });
    } else {
      setEditingItem(null);
      setLinkForm({
        section_id: '',
        title: '',
        url: '',
        icon: '',
        sort_order: 0
      });
    }
    setShowLinkModal(true);
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setEditingItem(null);
    setLinkForm({
      section_id: '',
      title: '',
      url: '',
      icon: '',
      sort_order: 0
    });
  };

  const openSocialModal = (social = null) => {
    if (social) {
      setEditingItem(social);
      setSocialForm({
        name: social.name,
        icon: social.icon,
        url: social.url,
        sort_order: social.sort_order
      });
    } else {
      setEditingItem(null);
      setSocialForm({
        name: '',
        icon: '',
        url: '',
        sort_order: 0
      });
    }
    setShowSocialModal(true);
  };

  const closeSocialModal = () => {
    setShowSocialModal(false);
    setEditingItem(null);
    setSocialForm({
      name: '',
      icon: '',
      url: '',
      sort_order: 0
    });
  };

  // Get section name by ID
  const getSectionName = (sectionId) => {
    const section = footerData.sections.find(s => s.id === sectionId);
    return section ? section.title : 'Unknown Section';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Footer Management</h1>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="text-green-800 dark:text-green-200">{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-800 dark:text-red-200">{error}</span>
        </div>
      )}

      {/* Footer Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Footer Settings
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Footer Description
            </label>
            <textarea
              value={settingsForm.footer_description}
              onChange={(e) => setSettingsForm(prev => ({ ...prev, footer_description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              rows={3}
              placeholder="Enter footer description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Copyright Text
            </label>
            <input
              type="text"
              value={settingsForm.footer_copyright}
              onChange={(e) => setSettingsForm(prev => ({ ...prev, footer_copyright: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter copyright text..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Contact Email
            </label>
            <input
              type="email"
              value={settingsForm.footer_email}
              onChange={(e) => setSettingsForm(prev => ({ ...prev, footer_email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter contact email..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Contact Phone
            </label>
            <input
              type="text"
              value={settingsForm.footer_phone}
              onChange={(e) => setSettingsForm(prev => ({ ...prev, footer_phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter contact phone number..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Contact Address
            </label>
            <textarea
              value={settingsForm.footer_address}
              onChange={(e) => setSettingsForm(prev => ({ ...prev, footer_address: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              rows={2}
              placeholder="Enter contact address..."
            />
          </div>

          <button
            onClick={handleSettingsSave}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Settings
          </button>
        </div>
      </div>

      {/* Footer Sections */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Link className="w-5 h-5" />
            Footer Sections
          </h2>
          <button
            onClick={() => openSectionModal()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-md flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Section
          </button>
        </div>

        <div className="space-y-3">
          {footerData.sections.map((section) => (
            <div key={section.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">{section.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Type: {section.section_type}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openSectionModal(section)}
                  className="text-blue-600 hover:text-blue-700 p-1"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleSectionDelete(section.id)}
                  className="text-red-600 hover:text-red-700 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Links */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Link className="w-5 h-5" />
            Footer Links
          </h2>
          <button
            onClick={() => openLinkModal()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-md flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Link
          </button>
        </div>

        <div className="space-y-3">
          {footerData.links.map((link) => (
            <div key={link.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Section: {getSectionName(link.section_id)} | URL: {link.url || 'N/A'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openLinkModal(link)}
                  className="text-blue-600 hover:text-blue-700 p-1"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleLinkDelete(link.id)}
                  className="text-red-600 hover:text-red-700 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Social Links
          </h2>
          <button
            onClick={() => openSocialModal()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-md flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Social Link
          </button>
        </div>

        <div className="space-y-3">
          {footerData.socialLinks.map((social) => (
            <div key={social.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">{social.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Icon: {social.icon} | URL: {social.url}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openSocialModal(social)}
                  className="text-blue-600 hover:text-blue-700 p-1"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleSocialDelete(social.id)}
                  className="text-red-600 hover:text-red-700 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section Modal */}
      {showSectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingItem ? 'Edit Section' : 'Add Section'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section Type
                </label>
                <select
                  value={sectionForm.section_type}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, section_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="services">Services</option>
                  <option value="products">Products</option>
                  <option value="company">Company</option>
                  <option value="support">Support</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={sectionForm.title}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter section title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={sectionForm.sort_order}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleSectionSave}
                disabled={saving}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingItem ? 'Update' : 'Create'}
              </button>
              <button
                onClick={closeSectionModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingItem ? 'Edit Link' : 'Add Link'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section
                </label>
                <select
                  value={linkForm.section_id}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, section_id: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select Section</option>
                  {footerData.sections.map((section) => (
                    <option key={section.id} value={section.id}>{section.title}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={linkForm.title}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter link title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL
                </label>
                <input
                  type="text"
                  value={linkForm.url}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter URL..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <input
                  type="text"
                  value={linkForm.icon}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter icon name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={linkForm.sort_order}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleLinkSave}
                disabled={saving}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingItem ? 'Update' : 'Create'}
              </button>
              <button
                onClick={closeLinkModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Modal */}
      {showSocialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingItem ? 'Edit Social Link' : 'Add Social Link'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={socialForm.name}
                  onChange={(e) => setSocialForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter social media name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <input
                  type="text"
                  value={socialForm.icon}
                  onChange={(e) => setSocialForm(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter icon name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL
                </label>
                <input
                  type="url"
                  value={socialForm.url}
                  onChange={(e) => setSocialForm(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter social media URL..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={socialForm.sort_order}
                  onChange={(e) => setSocialForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleSocialSave}
                disabled={saving}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingItem ? 'Update' : 'Create'}
              </button>
              <button
                onClick={closeSocialModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminSectionWrapper>
  );
};

export default FooterManagement; 